import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Calendar, ChevronRight, Trophy } from "lucide-react";
import SiteNav from "@/components/SiteNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_BG = "https://customer-assets.emergentagent.com/job_fight-forecast-1/artifacts/tf9we5d6_hamara8-220.jpg";
const EVENT_LOGO = "https://customer-assets.emergentagent.com/job_fight-forecast-1/artifacts/rof1u6za_LOGO_HAMARA_TUMMA.png";
const PLACEHOLDER_FIGHTER = "https://customer-assets.emergentagent.com/job_fight-forecast-1/artifacts/rof1u6za_LOGO_HAMARA_TUMMA.png";

function ArchivedFightCard({ fight }) {
  const { fighter1, fighter2, weight_class, fight_type, result, result_method } = fight;
  const isDraw = result === "draw";
  const winnerName = result === "fighter1" ? fighter1.name : result === "fighter2" ? fighter2.name : null;

  return (
    <div className="bg-[#121212] border border-white/10 overflow-hidden" data-testid={`archived-fight-${fight.id}`}>
      {/* Fight info */}
      <div className="px-3 pt-3 pb-1 text-center space-y-0.5">
        {fight_type && <span className="text-[9px] uppercase tracking-[0.2em] text-[#FF3B30] font-bold block">{fight_type}</span>}
        <span className="text-[9px] uppercase tracking-[0.2em] text-[#A1A1AA] font-bold block">{weight_class}</span>
      </div>

      {/* Result banner */}
      {isDraw ? (
        <div className="bg-white/5 border-y border-white/10 px-3 py-1.5 text-center">
          <span className="text-xs font-bold text-white/60 uppercase">Tasapeli</span>
          {result_method && <span className="text-[10px] text-white/40 ml-2">({result_method})</span>}
        </div>
      ) : winnerName ? (
        <div className="bg-white/5 border-y border-white/10 px-3 py-1.5 text-center">
          <div className="flex items-center justify-center gap-1.5">
            <Trophy className="w-3 h-3 text-white" />
            <span className="text-xs font-bold text-white uppercase">{winnerName}</span>
          </div>
          {result_method && <p className="text-[10px] text-white/50">{result_method}</p>}
        </div>
      ) : (
        <div className="border-y border-white/10 px-3 py-1.5 text-center">
          <span className="text-[10px] text-white/30 uppercase">Ei tulosta</span>
        </div>
      )}

      {/* Fighters */}
      <div className="flex items-stretch">
        <div className={`flex-1 p-2 pb-3 text-center border-r border-white/5 ${result === "fighter1" ? "bg-white/5" : result && result !== "draw" ? "opacity-40" : ""}`}>
          <div className={`w-16 h-16 mx-auto mb-2 overflow-hidden rounded-full ${result === "fighter1" ? "ring-2 ring-white" : ""}`}>
            <img src={fighter1.image_url || PLACEHOLDER_FIGHTER} alt={fighter1.name} className={`w-full h-full ${fighter1.image_url ? "object-cover object-top grayscale" : "object-contain p-2 opacity-30"}`} />
          </div>
          <h3 className="text-sm uppercase tracking-tight leading-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{fighter1.name}</h3>
          {fighter1.record && <p className="text-[10px] text-white/50">{fighter1.record}</p>}
        </div>
        <div className="flex items-center justify-center w-8 shrink-0">
          <span className="text-xs text-white/30 font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>VS</span>
        </div>
        <div className={`flex-1 p-2 pb-3 text-center border-l border-white/5 ${result === "fighter2" ? "bg-white/5" : result && result !== "draw" ? "opacity-40" : ""}`}>
          <div className={`w-16 h-16 mx-auto mb-2 overflow-hidden rounded-full ${result === "fighter2" ? "ring-2 ring-white" : ""}`}>
            <img src={fighter2.image_url || PLACEHOLDER_FIGHTER} alt={fighter2.name} className={`w-full h-full ${fighter2.image_url ? "object-cover object-top grayscale" : "object-contain p-2 opacity-30"}`} />
          </div>
          <h3 className="text-sm uppercase tracking-tight leading-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{fighter2.name}</h3>
          {fighter2.record && <p className="text-[10px] text-white/50">{fighter2.record}</p>}
        </div>
      </div>
    </div>
  );
}

export default function ArchivePage() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/events`).then(res => setEvents(res.data)).catch(err => console.error("Failed to load events:", err)).finally(() => setLoading(false));
  }, []);

  const handleSelectEvent = async (eventId) => {
    try {
      const res = await axios.get(`${API}/events/${eventId}`);
      setSelectedEvent(res.data);
    } catch {
      setSelectedEvent(null);
    }
  };

  const sortedFights = useMemo(() => 
    (selectedEvent?.fights || []).slice().sort((a, b) => (a.fight_order || 0) - (b.fight_order || 0)),
    [selectedEvent]
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero */}
      <section className="relative py-10 sm:py-14 flex flex-col items-center justify-center overflow-hidden"
        style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "30% 25%" }}>
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.6) 75%, rgba(10,10,10,1) 100%)" }} />
        <div className="relative z-10 text-center px-4">
          <Link to="/"><img src={EVENT_LOGO} alt="Hamara MMA" className="h-10 sm:h-14 mx-auto mb-4 object-contain" /></Link>
          <p className="text-white/50 text-xs lg:text-sm">Aiempien tapahtumien tulokset</p>
          <SiteNav />
        </div>
      </section>

      <section className="py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="h-px flex-1 bg-white/10" />
            <h2 className="text-2xl sm:text-3xl uppercase tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }} data-testid="archive-heading">
              Menneet tapahtumat
            </h2>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          {loading ? (
            <div className="text-center py-12 text-[#A1A1AA]">Ladataan...</div>
          ) : events.length === 0 ? (
            <div className="text-center py-16" data-testid="no-archives">
              <Calendar className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" />
              <p className="text-[#A1A1AA]">Ei arkistoituja tapahtumia</p>
            </div>
          ) : selectedEvent ? (
            /* Event detail view */
            <div data-testid="archive-detail">
              <button onClick={() => setSelectedEvent(null)}
                className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors mb-6"
                data-testid="back-to-events">
                <ChevronRight className="w-4 h-4 rotate-180" /> Takaisin
              </button>
              <h3 className="text-3xl sm:text-4xl uppercase tracking-tight mb-8" style={{ fontFamily: "'Bebas Neue', sans-serif" }} data-testid="archive-event-name">
                {selectedEvent.name}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {sortedFights.map(fight => <ArchivedFightCard key={fight.id} fight={fight} />)}
              </div>
            </div>
          ) : (
            /* Event list */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="archive-list">
              {events.map(event => (
                <button key={event.id} onClick={() => handleSelectEvent(event.id)}
                  className="bg-[#121212] border border-white/10 p-6 text-left hover:border-white/30 hover:bg-white/5 transition-all group"
                  data-testid={`archive-event-${event.id}`}>
                  <h3 className="text-2xl uppercase tracking-tight mb-2 group-hover:text-[#FF3B30] transition-colors"
                    style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{event.name}</h3>
                  <div className="flex items-center gap-1 text-xs text-[#FF3B30] opacity-0 group-hover:opacity-100 transition-opacity">
                    Katso tulokset <ChevronRight className="w-3 h-3" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-4 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="w-10 h-10 border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-colors">
              <ChevronRight className="w-5 h-5 -rotate-90" />
            </button>
            <span className="text-xs text-[#A1A1AA]">Hamara MMA</span>
          </div>
          <Link to="/admin" className="text-xs text-white/30 hover:text-white/50 transition-colors">Admin</Link>
        </div>
      </footer>
    </div>
  );
}
